import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { complaintAPI, customerAPI } from '../../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { MessageSquare, Plus, ClipboardList, Tv, Wifi, Paperclip, X } from 'lucide-react';

const CATV_CATEGORIES = [
  { value: 'e61_no_signal', label: 'E61 - No Signal' },
  { value: 'e51_channel_error', label: 'E51/999 Channel Error' },
  { value: 'channel_stuck', label: 'ચેનલ ચોટે છે' },
  { value: 'colors_only', label: 'Colors Only' },
  { value: 'relocation_transfer', label: 'Relocation/Transfer' },
];
const BB_CATEGORIES = [
  { value: 'no_internet', label: 'No Internet' },
  { value: 'slow_internet', label: 'Slow Internet' },
  { value: 'red_light_router', label: 'Red Light in Router' },
  { value: 'cable_cut', label: 'Cable Cut' },
  { value: 'router_issue', label: 'Router Issue' },
];
const COMMON_CATEGORIES = [
  { value: 'billing_dispute', label: 'Billing Issue' },
  { value: 'other', label: 'Other' },
];

export default function CustomerComplaints() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [customer, setCustomer] = useState(null);
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(searchParams.get('new') === '1');
  const [selected, setSelected] = useState(null);
  const [serviceType, setServiceType] = useState('catv');
  const [form, setForm] = useState({ category: '', description: '' });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const [complaintsRes, meRes] = await Promise.all([
        complaintAPI.getAll({ limit: 20 }),
        customerAPI.getMe(),
      ]);
      setComplaints(complaintsRes.data.data || []);
      const me = meRes.data.data;
      setCustomer(me);
      const linked = me.linkedAccount || null;
      const catvSource = me.customerType !== 'broadband' ? me : linked;
      const hasCatv = (catvSource?.customerType === 'catv' || catvSource?.customerType === 'both') && !!catvSource?.catv;
      setServiceType(hasCatv ? 'catv' : 'broadband');
    } catch { toast.error('Failed to load complaints'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const linked = customer?.linkedAccount || null;
  const bbSource = customer?.customerType !== 'catv' ? customer : linked;
  const catvSource = customer?.customerType !== 'broadband' ? customer : linked;
  const hasBB = (bbSource?.customerType === 'broadband' || bbSource?.customerType === 'both') && !!bbSource?.broadband;
  const hasCatv = (catvSource?.customerType === 'catv' || catvSource?.customerType === 'both') && !!catvSource?.catv;
  const account = serviceType === 'catv' ? catvSource : bbSource;

  const categoryOptions = [
    ...(serviceType === 'catv' ? CATV_CATEGORIES : BB_CATEGORIES),
    ...COMMON_CATEGORIES,
  ];

  const selectService = (key) => {
    setServiceType(key);
    setForm({ category: '', description: form.description });
  };

  const handleFilesChange = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (picked.length) setFiles(f => [...f, ...picked].slice(0, 5));
  };

  const removeFile = (idx) => setFiles(f => f.filter((_, i) => i !== idx));

  const resetForm = () => {
    setForm({ category: '', description: '' });
    setFiles([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!account) { toast.error(`You don't have a ${serviceType === 'catv' ? 'CableTV' : 'Broadband'} connection on this account`); return; }
    if (!files.length) { toast.error('Please attach a photo or video of the issue'); return; }
    const category = form.category || categoryOptions[0].value;
    const title = categoryOptions.find(c => c.value === category)?.label || 'Complaint';

    setSubmitting(true);
    try {
      const payload = new FormData();
      payload.append('customer', account._id);
      payload.append('serviceType', serviceType);
      payload.append('category', category);
      payload.append('title', title);
      payload.append('description', form.description);
      files.forEach(f => payload.append('media', f));
      await complaintAPI.create(payload);
      toast.success('Complaint submitted! Our team will contact you soon.');
      setShowNew(false);
      resetForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit');
    } finally { setSubmitting(false); }
  };

  const STATUS_COLOR = { open: '#dc2626', in_progress: '#d97706', resolved: '#059669', closed: '#64748b' };

  return (
    <div>
      <div className="flex justify-between items-center mb-6" style={{ flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 700 }}>Support Tickets</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>Raise and track your complaints</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={() => navigate('/portal/requests?new=1')}>
            <ClipboardList size={14} /> New Request
          </button>
          <button className="btn btn-primary" onClick={() => setShowNew(true)}>
            <Plus size={14} /> New Complaint
          </button>
        </div>
      </div>

      {/* New complaint form */}
      {showNew && (
        <div className="card mb-6">
          <div className="card-header">
            <h3>Submit a Complaint</h3>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowNew(false)}>Cancel</button>
          </div>
          <div className="card-body">
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: '.8rem', color: '#1e3a8a' }}>
              Want to disconnect, reconnect, change your package, or move address? Use the <strong>Requests</strong> tab instead — this form is for faults/issues.
            </div>

            {hasBB && hasCatv && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                <button type="button"
                  className={`btn btn-${serviceType === 'catv' ? 'primary' : 'secondary'} btn-sm`}
                  onClick={() => selectService('catv')}>
                  <Tv size={13} /> CableTV
                </button>
                <button type="button"
                  className={`btn btn-${serviceType === 'broadband' ? 'primary' : 'secondary'} btn-sm`}
                  onClick={() => selectService('broadband')}>
                  <Wifi size={13} /> Broadband
                </button>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Issue Type *</label>
                <select className="form-control" value={form.category || categoryOptions[0].value}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                  {categoryOptions.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Description *</label>
                <textarea className="form-control" rows={4} value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))} required
                  placeholder="Please describe the issue in detail. When did it start? What have you tried?" />
              </div>

              <div className="form-group">
                <label className="form-label">Photo / Video *</label>
                <label className="btn btn-secondary btn-sm" style={{ display: 'inline-flex', cursor: 'pointer' }}>
                  <Paperclip size={13} /> Attach media
                  <input type="file" accept="image/*,video/*" multiple onChange={handleFilesChange} style={{ display: 'none' }} />
                </label>
                {files.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                    {files.map((f, i) => (
                      <span key={i} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '.78rem',
                        padding: '5px 10px', borderRadius: 8, background: 'var(--bg)', border: '1px solid var(--border)',
                      }}>
                        {f.name}
                        <X size={12} style={{ cursor: 'pointer' }} onClick={() => removeFile(i)} />
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowNew(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Complaint'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Complaint list */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : complaints.length === 0 ? (
        <div className="empty-state card" style={{ padding: 48 }}>
          <MessageSquare size={32} />
          <h3>No complaints yet</h3>
          <p>If you're experiencing any issues, click "New Complaint" to let us know.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {complaints.map(c => (
            <div key={c._id} className="card" style={{ cursor: 'pointer' }} onClick={() => setSelected(selected === c._id ? null : c._id)}>
              <div style={{ padding: '14px 18px', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span className="font-mono" style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{c.ticketNumber}</span>
                    <span style={{ fontSize: '.72rem', padding: '2px 8px', borderRadius: 12, fontWeight: 600, background: `${STATUS_COLOR[c.status]}20`, color: STATUS_COLOR[c.status], textTransform: 'capitalize' }}>
                      {c.status?.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <div style={{ fontWeight: 600, fontSize: '.95rem' }}>{c.title}</div>
                  <div style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginTop: 4, textTransform: 'capitalize' }}>
                    {c.category?.replace(/_/g, ' ')} · {format(new Date(c.createdAt), 'dd MMM yyyy')}
                  </div>
                </div>
                {c.assignedTo && (
                  <div style={{ textAlign: 'right', fontSize: '.78rem', color: 'var(--text-muted)' }}>
                    <div>Assigned to</div>
                    <div style={{ fontWeight: 600, color: 'var(--text)' }}>{c.assignedTo.name}</div>
                  </div>
                )}
              </div>
              {selected === c._id && (
                <div style={{ padding: '0 18px 14px', borderTop: '1px solid var(--border)', paddingTop: 14 }}>
                  <p style={{ fontSize: '.85rem', marginBottom: 12 }}>{c.description}</p>
                  {c.attachments?.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                      {c.attachments.map((url, i) => (
                        <a key={i} href={url} target="_blank" rel="noreferrer" style={{
                          fontSize: '.78rem', padding: '5px 10px', borderRadius: 8,
                          background: 'var(--bg)', border: '1px solid var(--border)', textDecoration: 'none', color: 'var(--primary)',
                        }}>
                          Attachment {i + 1}
                        </a>
                      ))}
                    </div>
                  )}
                  {c.resolutionNotes && (
                    <div style={{ background: '#d1fae5', padding: '10px 14px', borderRadius: 8, fontSize: '.85rem' }}>
                      <strong>Resolution:</strong> {c.resolutionNotes}
                    </div>
                  )}
                  {c.timeline?.slice(-3).reverse().map((t, i) => (
                    <div key={i} style={{ fontSize: '.78rem', color: 'var(--text-muted)', padding: '4px 0', borderTop: i > 0 ? '1px solid var(--border)' : 'none', marginTop: 4 }}>
                      <strong>{t.action}</strong> — {t.description} · {format(new Date(t.timestamp), 'dd/MM/yy HH:mm')}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
